import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/foundation/api/client";
import { useAuth } from "@/foundation/auth/AuthContext";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { resolveActiveCompanyId } from "@/foundation/context/activeCompanyResolver";

const ActiveCompanyContext = createContext(null);
const STORAGE_KEY = "gi_presenze_active_company_id";
const GLOBAL_ROLES = new Set(["admin", "super_admin", "external_consultant"]);

function readStoredCompanyId() {
  try { return localStorage.getItem(STORAGE_KEY) || ""; }
  catch { return ""; }
}

export function ActiveCompanyProvider({ children }) {
  const { user } = useAuth();
  const isGlobalCompanyRole = GLOBAL_ROLES.has(user?.role);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyIdState] = useState(readStoredCompanyId);
  const [loading, setLoading] = useState(false);

  const setActiveCompanyId = useCallback((companyId) => {
    const next = companyId || "";
    setActiveCompanyIdState(next);
    if (!isGlobalCompanyRole) return;
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* storage non disponibile: resta valido per la sessione */ }
  }, [isGlobalCompanyRole]);

  useEffect(() => {
    if (!user) {
      setCompanies([]);
      return;
    }
    if (!isGlobalCompanyRole) {
      setCompanies([]);
      setActiveCompanyIdState(user.company_id || "");
      return;
    }

    let cancelled = false;
    setLoading(true);
    api.get("/companies")
      .then(({ data }) => {
        if (cancelled) return;
        const sorted = sortCompaniesIt(Array.isArray(data) ? data : []);
        setCompanies(sorted);

        const stored = readStoredCompanyId();
        const next = resolveActiveCompanyId(sorted, stored);
        setActiveCompanyId(next);
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user, isGlobalCompanyRole, setActiveCompanyId]);

  const activeCompany = useMemo(
    () => companies.find((company) => company.id === activeCompanyId) || null,
    [companies, activeCompanyId],
  );

  const value = useMemo(() => ({
    companies,
    activeCompany,
    activeCompanyId,
    setActiveCompanyId,
    isGlobalCompanyRole,
    loading,
  }), [companies, activeCompany, activeCompanyId, setActiveCompanyId, isGlobalCompanyRole, loading]);

  return (
    <ActiveCompanyContext.Provider value={value}>
      {children}
    </ActiveCompanyContext.Provider>
  );
}

export function useActiveCompany() {
  const value = useContext(ActiveCompanyContext);
  if (!value) throw new Error("useActiveCompany deve essere usato dentro ActiveCompanyProvider");
  return value;
}

/**
 * Compatibilità per le pagine: super amministratore usa l'azienda globale;
 * gli altri ruoli conservano il proprio perimetro aziendale.
 */
export function useCompanySelection() {
  const { user } = useAuth();
  const context = useActiveCompany();
  const [localCompanyId, setLocalCompanyId] = useState(user?.company_id || "");

  useEffect(() => {
    if (!context.isGlobalCompanyRole) setLocalCompanyId(user?.company_id || "");
  }, [context.isGlobalCompanyRole, user?.company_id]);

  return context.isGlobalCompanyRole
    ? [context.activeCompanyId, context.setActiveCompanyId]
    : [localCompanyId, setLocalCompanyId];
}
